import React, { useEffect, useState } from 'react';
import { Grow, IconButton, Tooltip, Paper, Popper, useTheme } from '@mui/material';
import { ContentCopyOutlined } from '@mui/icons-material';

type OverridableProps = Omit<React.ComponentProps<typeof IconButton>, 'onClick'>;

const CopyButton: React.FC<
  {
    text: string;
    timeout?: number;
  } & OverridableProps
> = ({ text, timeout = 1000, ...props }) => {
  const theme = useTheme();

  const [copyButtonEl, setCopyButtonEl] = useState<HTMLButtonElement | null>(null);
  const [copiedAlertOpen, setCopiedAlertOpen] = useState(false);
  useEffect(() => {
    if (!copiedAlertOpen) {
      return;
    }
    const doTimeout = setTimeout(() => {
      setCopiedAlertOpen(false);
    }, timeout);

    return () => clearTimeout(doTimeout);
  }, [copiedAlertOpen, setCopiedAlertOpen, timeout]);

  return (
    <>
      <Tooltip title='Copy' placement='top'>
        <IconButton
          onClick={e => {
            setCopyButtonEl(e.currentTarget);
            navigator.clipboard.writeText(text).then(() => {
              setCopiedAlertOpen(true);
            });
          }}
          sx={{ flex: 0, ...props.sx }}
          {...props}
        >
          <ContentCopyOutlined />
        </IconButton>
      </Tooltip>

      <Popper open={copiedAlertOpen} anchorEl={copyButtonEl} placement='top' transition sx={{ zIndex: 1000 }}>
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            timeout={
              copiedAlertOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen
            }
          >
            <Paper
              sx={{
                backgroundColor: theme.palette.success.main,
                color: theme.palette.success.contrastText,
                paddingX: theme.spacing(2),
                paddingY: theme.spacing(1),
                marginBottom: theme.spacing(1),
              }}
            >
              Copied!
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default CopyButton;
